import type { Board } from 'chessboard/model/board/types';
import type { Component } from 'types/component';
import type {
  ConfirmationCallback as ConfirmationAPICallback,
  Confirmations as ConfirmationsAPI,
} from 'utils/confirmations';
import type { HorizontalMoveList } from 'components/horizontal-move-list/config/types';
import type { Game } from 'chessboard/model/game/types';
import type { GameAPI } from 'chessboard/types';
import type { MoveList } from 'chessboard/model/move-list/types';
import type { MoveNode } from 'components/horizontal-move-list/move-node/config/types';

export namespace ContextMenu {
  export interface API {
    destroy(): void;
  }

  /**
   * All possible actions on the context menu
   */
  export enum Actions {
    ClearAnalysis = 'ClearAnalysis',
    DeleteComment = 'DeleteComment',
    DeleteCommentBefore = 'DeleteCommentBefore',
    DeleteMove = 'DeleteMove',
    DeleteVariation = 'DeleteVariation',
    InsertComment = 'InsertComment',
    InsertCommentBefore = 'InsertCommentBefore',
    InsertContinuation = 'InsertContinuation',
    MoveVariationDown = 'MoveVariationDown',
    MoveVariationUp = 'MoveVariationUp',
    PromoteVariation = 'PromoteVariation',
    RemoveAnnotation = 'RemoveAnnotation',
    RemoveBegin = 'RemoveBegin',
    RemoveEnd = 'RemoveEnd',
    RemoveAdditionalAnnotation = 'RemoveAdditionalAnnotation',
    RemoveFocus = 'RemoveFocus',
    SetBegin = 'SetBegin',
    SetEnd = 'SetEnd',
    SetFocus = 'SetFocus',
  }

  /**
   * Collection of ContextMenu actions
   */
  export type ActionsCollection = {
    [key in ContextMenu.Actions]?: ContextMenu.Action;
  };

  /**
   * This is separate from the regular actions because these will not show in the menu.
   * These are simply action types for the two types of annotations.
   */
  export enum AnnotationActions {
    AddAnnotation = 'AddAnnotation',
    AddAdditionalAnnotation = 'AddAdditionalAnnotation',
  }

  export interface Action {
    /**
     * The action to emit when this is clicked.
     */
    action: string;

    /**
     * The handler for the action
     */
    handler: (node: Game.Move.Node, context: ContextMenu.HandlerParams<any>) => void;

    /**
     * A condition determining whether or not to show this action in the context menu
     */
    isVisible: (params: ContextMenu.IsVisibleParams) => boolean;

    /**
     * The display name of the action in the menu.
     */
    name: string;
  }

  /**
   * The params passed to a context menu `isVisible` function
   */
  export interface IsVisibleParams {
    api: GameAPI;
    node: Game.Move.Node;
    options: Board.Options;
    state: HorizontalMoveList.State;
  }

  /**
   * The potential keys to change on the context menu
   */
  export enum Keys {
    Actions = 'actions', // eslint-disable-line
    Translations = 'translations',
  }

  /**
   * Events emitted from the context menu component
   */
  export enum Events {
    /**
     * When a context menu action is clicked
     */
    ActionClicked = 'action-clicked',

    /**
     * When an annotation is clicked
     */
    AnnotationClicked = 'annotation-clicked',

    /**
     * When the context menu should close
     */
    Close = 'close-context-menu',
  }

  /**
   * The params passed to a context menu handler
   */
  export interface HandlerParams<Data> {
    context: HorizontalMoveList.Context;
    data: Data;
    event: Event;
    moveList: HorizontalMoveList.Component;
    /**
     * The move node component that was clicked to trigger the context menu
     */
    moveNodeComponent: MoveNode.API;
  }

  export namespace Plugin {
    export const Name = 'contextMenu';

    export interface API extends Confirmations {}

    export type Confirmations = ConfirmationsAPI<typeof Actions, { event: Event }>;
    export type ConfirmationCallback = ConfirmationAPICallback<typeof Actions, { event: Event }>;
  }
}

export namespace ContextMenuComponent {
  export interface API extends Component.API {
    getElement(): HTMLElement;
  }

  export interface Props {
    [ContextMenu.Keys.Actions]: ContextMenu.Action[];
    [ContextMenu.Keys.Translations]: MoveList.Translations;
  }

  export enum Classes {
    ActionName = 'action-name',
    Annotation = 'annotation',
    AnnotationsContainer = 'annotations-container',
    Component = 'move-node-context-menu', // eslint-disable-line
    Divider = 'divider',
  }
}
